/**
 * The platform's fallback voice for a failed `scripts.run` (#184 Phase 4).
 *
 * ── Why this exists ──────────────────────────────────────────────────────────
 *
 * An app can call `pdapp.scripts.run(...)` and do nothing with the failure — the
 * author never wrote the branch, or an AI generating the app dropped it. That is
 * a dead button: the user clicks and nothing visibly happens. Even the scaffold,
 * which does handle it, prints one string ("Export failed.") for the author's own
 * bug and for our container failing to start, because the app cannot tell those
 * apart and B26 strips the infra text in production anyway.
 *
 * So the platform speaks. The decision of WHETHER to speak is the server's — see
 * `apps/api/src/v1/author-notice.ts`, which gates on `readwrite` on the app
 * folder and on the failure being ours rather than the script's. This module is
 * only the throttle and the hand-off.
 *
 * ── Codes, never text ────────────────────────────────────────────────────────
 *
 * Nothing here is display copy. The consumer maps a code to its own strings, the
 * same contract `onDesignNotice` already has, and for a stronger reason: this
 * value rode a response through an untrusted iframe's relay, and painting
 * app-supplied strings as trusted chrome is the R21 #140 brand-spoof lesson.
 */

/**
 * Once per minute per (app, code, scriptPath) — the SAME window as the SDK's
 * `warnIfThrottled`, deliberately.
 *
 * That one is throttled because a runaway loop would flood the console. A toast
 * in the same loop floods the SCREEN, which is worse: the console scrolls, a
 * stack of toasts covers the app the person is trying to use. Reusing the window
 * rather than inventing a second one also means the two cannot disagree about
 * how noisy a broken app is allowed to be.
 *
 * `scriptPath` is part of the KEY and never rendered — an app that breaks two
 * different scripts should hear about both, and one that breaks the same script
 * in a loop should hear about it once.
 *
 * `appFolderId` is part of it for the SAME reason one rung out, and it is not
 * optional: this map is module-global, and every PDApp minted from the scaffold
 * ships the same `_scripts/*.py` names. Keyed on the path alone, two different
 * broken apps open in one tab suppress EACH OTHER for a minute — restoring the
 * dead button this whole mechanism exists to remove, on the app the person did
 * not just look at.
 */
const THROTTLE_MS = 60_000
const lastNoticedAt = new Map<string, number>()

/** Test seam. The map is module state so the throttle survives remounts, which
 *  is what makes it a throttle rather than a per-render formality. */
export function __resetAppRunNoticeThrottle(): void {
  lastNoticedAt.clear()
}

export interface AppRunNotice {
  code: string
  warnings?: string[]
  requestId?: string
}

/**
 * Read a server-authoritative run response and, if it carries a notice, hand it
 * to the consumer at most once a minute per script.
 *
 * Every field is re-validated rather than trusted. `body` reaches here from a
 * `fetch` the host itself made, so it IS the server's answer — but this function
 * is also the boundary a future caller might reuse with a body from somewhere
 * else, and a `code` that is not a string would otherwise become a React child.
 *
 * No-ops with no consumer wired. Deliberately silent, unlike `papan:openExternal`
 * which warns: a missing external-link handler DROPS a user action, while a
 * missing notice handler only means this surface has no chrome to paint one —
 * true of the share view and the version preview, by design.
 */
export function emitAppRunNotice(
  body: unknown,
  onNotice: ((notice: AppRunNotice) => void) | undefined,
  scriptPath?: string | null,
  appFolderId?: string | null,
  now: number = Date.now(),
): void {
  if (!onNotice) return
  const raw = (body as { authorNotice?: unknown } | null | undefined)?.authorNotice
  if (!raw || typeof raw !== 'object') return
  const { code, warnings, requestId } = raw as Record<string, unknown>
  if (typeof code !== 'string' || code.length === 0) return

  const key = `${appFolderId ?? ''}:${code}:${scriptPath ?? ''}`
  const prev = lastNoticedAt.get(key)
  if (prev !== undefined && now - prev < THROTTLE_MS) return
  lastNoticedAt.set(key, now)

  /**
   * FAIL-SOFT, and this is the load-bearing part rather than defensive habit.
   *
   * The one caller invokes this from inside the block whose `catch` answers the
   * app `network_error` — so a throw from the consumer (a stale handler, a
   * provider torn down mid-run) skipped the `postMessage` that follows and
   * reported a COMPLETED `ok: true` run as a transport failure. That is the
   * exact class `transport-error.ts` exists to prevent, arriving through the
   * chrome instead of the wire, and it lands on the SUCCESS path: the run whose
   * only problem was a dropped event becomes, to the app, a failed one.
   *
   * Swallowed HERE rather than at the call site, so it holds for every caller
   * including ones added later — and so it is assertable at all, which a `try`
   * wrapped around one line inside a 200-line message handler is not. The
   * SERVER half is already fail-soft for the same reason (`buildAuthorNotice`).
   *
   * The throttle window is spent either way: the mark is taken above, so a
   * consumer that throws on every call is not retried sixty times a minute.
   */
  try {
    onNotice({
      code,
      ...(Array.isArray(warnings)
        ? { warnings: warnings.filter((w): w is string => typeof w === 'string') }
        : {}),
      ...(typeof requestId === 'string' ? { requestId } : {}),
    })
  } catch (err) {
    console.warn('[app-run-notice] notice consumer threw:', err)
  }
}
